import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { renderUserAvatar } from "@/lib/avatar";
import { useAuth } from "@/lib/auth.jsx";
import { useDrawer } from "@/components/RightDrawer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Hash, Send, Users, Headphones, Settings, BarChart3,
} from "lucide-react";
import ChannelSettingsDialog from "@/components/ChannelSettingsDialog";
import AttachmentComposer from "@/components/AttachmentComposer";
import PollComposer from "@/components/PollComposer";
import MessageItem from "@/components/channel/MessageItem";
import ThreadPanel from "@/components/channel/ThreadPanel";
import MembersList from "@/components/channel/MembersList";

function dayStr(iso) {
  try {
    const d = new Date(iso); const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function ChannelView({ subscribe, wsSend, onOpenCall }) {
  const { channelId } = useParams();
  const { user } = useAuth();
  const { openDrawer } = useDrawer();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [members, setMembers] = useState([]);
  const [typing, setTyping] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  // Whether the current user can manage the currently loaded channel.
  const managerTitleRe = /\b(manager|director|head|lead|chief|founder|president|vp|ceo|cto|cfo|coo)\b/i;
  const canManageChannel = !!(
    channel && (
      ["owner", "admin"].includes(user?.role) ||
      managerTitleRe.test(user?.title || "") ||
      channel.created_by === user?.id
    )
  );

  // ── @mention autocomplete state ─────────────────────────────
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1); // index of the '@'

  // Departments derived from the current channel/org — used for group mentions
  // like `@sales` / `@engineering`. Group tokens live alongside user tokens
  // in the autocomplete popup.
  const departments = useMemo(() => {
    const map = new Map();
    for (const m of members) {
      const d = (m.department || "").trim();
      if (!d) continue;
      const key = d.toLowerCase();
      if (!map.has(key)) map.set(key, { name: d, member_ids: [] });
      map.get(key).member_ids.push(m.user_id);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [members]);

  // Group tokens that always exist (regardless of departments). "all",
  // "channel", and "here" all resolve to "everyone in this channel".
  const specialGroups = useMemo(
    () => [
      { key: "all", label: "@all", subtitle: `Notify all ${members.length} members` },
      { key: "channel", label: "@channel", subtitle: "Same as @all" },
      { key: "here", label: "@here", subtitle: "Everyone currently active" },
    ],
    [members.length]
  );

  const mentionCandidates = useMemo(() => {
    if (!mentionOpen) return [];
    const q = (mentionQuery || "").toLowerCase();

    // Group entries first — they read best at the top of the list.
    const groups = [
      ...specialGroups
        .filter((g) => !q || g.key.startsWith(q))
        .map((g) => ({
          kind: "special",
          key: g.key,
          user_id: `__grp_${g.key}`,
          user: { name: g.label },
          department: g.subtitle,
        })),
      ...departments
        .filter((d) => !q || d.name.toLowerCase().replace(/\s+/g, "").startsWith(q.replace(/\s+/g, "")))
        .map((d) => ({
          kind: "department",
          key: d.name,
          user_id: `__grp_dept_${d.name}`,
          user: { name: `@${d.name.replace(/\s+/g, "-").toLowerCase()}` },
          department: `Notify ${d.member_ids.length} in ${d.name}`,
          _member_ids: d.member_ids,
        })),
    ];

    const users = members
      .filter((m) => m.user_id !== user?.id)
      .filter((m) => {
        if (!q) return true;
        const n = (m.user?.name || "").toLowerCase();
        const e = (m.user?.email || "").toLowerCase();
        return n.includes(q) || e.startsWith(q);
      })
      .map((m) => ({ ...m, kind: "user" }));

    return [...groups, ...users].slice(0, 8);
  }, [mentionOpen, mentionQuery, members, user?.id, departments, specialGroups]);

  useEffect(() => {
    if (!channelId) return;
    (async () => {
      const [chAll, mems] = await Promise.all([
        api.get("/channels"),
        api.get("/orgs/current/members"),
      ]);
      setChannel(chAll.data.find((c) => c.id === channelId));
      setMembers(mems.data);
      const { data } = await api.get(`/channels/${channelId}/messages`);
      setMessages(data);
      await api.post(`/channels/${channelId}/read`);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
    })();
  }, [channelId]);

  // subscribe realtime
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type, payload) => {
      if (type === "message.new" && payload?.channel_id === channelId && !payload.parent_id) {
        setMessages((prev) => [...prev, payload]);
        setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 20);
        // If the user is currently looking at this channel, auto-mark read
        // so the sidebar badge never accumulates.
        api.post(`/channels/${channelId}/read`).catch(() => {});
      } else if (type === "message.update" && payload?.message?.channel_id === channelId) {
        setMessages((prev) => prev.map((m) => (m.id === payload.message.id ? { ...m, ...payload.message } : m)));
      } else if (type === "message.delete") {
        setMessages((prev) => prev.filter((m) => m.id !== payload.message_id));
      } else if (type === "message.reaction") {
        setMessages((prev) => prev.map((m) => (m.id === payload.message_id ? { ...m, reactions: payload.reactions } : m)));
      } else if (type === "typing" && payload?.channel_id === channelId && payload.user_id !== user?.id) {
        setTyping((t) => ({ ...t, [payload.user_id]: { name: payload.user_name, ts: Date.now() } }));
        setTimeout(() => setTyping((t) => { const c = { ...t }; delete c[payload.user_id]; return c; }), 2500);
      }
    });
  }, [subscribe, channelId, user?.id]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text;

    // Parse @mentions — group tokens expand to full member ID lists;
    // regular @name tokens resolve against the org member list.
    const tokens = [...t.matchAll(/@([a-zA-Z0-9._-]+)/g)].map((m) => m[1]);
    const lower = (s) => s.toLowerCase();
    const otherIds = members.filter((m) => m.user_id !== user?.id).map((m) => m.user_id);

    const idSet = new Set();
    for (const tok of tokens) {
      const lt = lower(tok);
      if (lt === "all" || lt === "channel" || lt === "here") {
        otherIds.forEach((id) => idSet.add(id));
        continue;
      }
      const dept = departments.find((d) => lower(d.name).replace(/\s+/g, "-") === lt);
      if (dept) {
        dept.member_ids.filter((id) => id !== user?.id).forEach((id) => idSet.add(id));
        continue;
      }
      const hit = members.find((m) =>
        lower(m.user?.email || "").startsWith(lt) ||
        lower((m.user?.name || "").split(" ")[0] || "") === lt
      );
      if (hit && hit.user_id !== user?.id) idSet.add(hit.user_id);
    }

    setText("");
    closeMention();
    const { data } = await api.post(`/channels/${channelId}/messages`, {
      text: t,
      mentions: Array.from(idSet),
    });
    // optimistic: WS should also push
    setMessages((prev) => (prev.find((m) => m.id === data.id) ? prev : [...prev, data]));
    setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 20);
  };

  const closeMention = () => {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(-1);
    setMentionIndex(0);
  };

  // Detect an active `@query` token at the cursor position and open the popup.
  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    const cursor = e.target.selectionStart ?? value.length;
    // Look backwards from cursor for the nearest '@' with no whitespace between.
    let i = cursor - 1;
    while (i >= 0) {
      const ch = value[i];
      if (ch === "@") break;
      if (/\s/.test(ch)) { i = -1; break; }
      i--;
    }
    if (i >= 0 && value[i] === "@") {
      const prev = i > 0 ? value[i - 1] : " ";
      // Only trigger when '@' is at start or after whitespace to avoid emails.
      if (/\s|^/.test(prev) || i === 0) {
        setMentionStart(i);
        setMentionQuery(value.slice(i + 1, cursor));
        setMentionOpen(true);
        setMentionIndex(0);
        return;
      }
    }
    closeMention();
  };

  const selectMention = (member) => {
    if (!member) return;
    let inserted;
    if (member.kind === "special") {
      inserted = `@${member.key} `;
    } else if (member.kind === "department") {
      inserted = `@${member.key.replace(/\s+/g, "-").toLowerCase()} `;
    } else {
      const nameToken = ((member.user?.name || member.user?.email || "user").split(" ")[0] || "user")
        .replace(/[^\w\.]/g, "");
      inserted = `@${nameToken} `;
    }
    const before = text.slice(0, mentionStart);
    const after = text.slice(mentionStart + 1 + mentionQuery.length);
    const newText = `${before}${inserted}${after}`;
    setText(newText);
    closeMention();
    // Restore cursor position after React re-render
    const nextCursor = before.length + inserted.length;
    setTimeout(() => {
      const el = textareaRef.current;
      if (el) { el.focus(); el.setSelectionRange(nextCursor, nextCursor); }
    }, 0);
  };

  const onKey = (e) => {
    if (mentionOpen && mentionCandidates.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex((i) => (i + 1) % mentionCandidates.length); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); selectMention(mentionCandidates[mentionIndex]); return; }
      if (e.key === "Escape")    { e.preventDefault(); closeMention(); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
    else if (wsSend) wsSend({ type: "typing", channel_id: channelId });
  };

  const react = async (m, emoji) => {
    const { data } = await api.post(`/messages/${m.id}/reactions`, { emoji });
    setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, reactions: data.reactions } : x)));
  };

  const uploadFile = async ({ attachment, caption }) => {
    // Caption + attachment posted as a single message so both land together
    // in the timeline (WhatsApp style). Empty caption falls back to a short
    // filename tag so the message row still has readable text content.
    await api.post(`/channels/${channelId}/messages`, {
      text: caption || `📎 ${attachment.filename}`,
      attachments: [attachment],
    });
  };

  const createPoll = async (poll) => {
    await api.post(`/channels/${channelId}/messages`, {
      text: `📊 ${poll.question}`,
      poll,
    });
  };

  const openThread = (msg) => {
    openDrawer("Thread", msg.id, <ThreadPanel msgId={msg.id} />);
  };

  const convertToTask = async (msg) => {
    await api.post("/tasks/from-message", null, { params: { message_id: msg.id } });
    toast.success("Task created from message");
  };

  const startHuddle = async () => {
    try {
      // Voice-first huddle. Users can toggle their camera on inside the call.
      const { data } = await api.post("/calls/start", {
        room: `huddle-${channelId}`,
        kind: "voice",
        channel_id: channelId,
      });
      onOpenCall(data);
      // Post an announcement so anyone else in the channel can jump in.
      await api.post(`/channels/${channelId}/messages`, {
        text: `🎧 ${user?.name || "Someone"} started a huddle. Tap the huddle button up top to join.`,
      });
    } catch (e) {
      toast.error("Could not start the huddle");
    }
  };

  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const d = dayStr(m.created_at);
      if (d !== lastDay) { out.push({ divider: d }); lastDay = d; }
      out.push(m);
    }
    return out;
  }, [messages]);

  if (!channel) return <div className="p-6 text-sm text-zinc-500">Loading channel…</div>;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="channel-view">
      <div className="h-14 border-b border-[var(--border-subtle)] flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-2">
          <Hash className="w-5 h-5 text-zinc-500"/>
          <div>
            <div className="font-semibold" data-testid="channel-name">{channel.name}</div>
            <div className="text-xs text-zinc-500">{channel.description || "No description"}</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={startHuddle}
            data-testid="channel-start-huddle"
            className="h-8 gap-1.5 text-zinc-700 hover:text-[var(--brand-accent)] hover:bg-orange-50"
            title="Start a huddle — a quick voice room for this channel"
          >
            <Headphones className="w-4 h-4"/>
            <span className="text-xs font-medium">Start huddle</span>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => openDrawer("Members", channel.id, <MembersList members={members}/>)} data-testid="channel-members">
            <Users className="w-4 h-4"/>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            data-testid="channel-settings-open"
            title="Channel settings"
          >
            <Settings className="w-4 h-4"/>
          </Button>
        </div>
      </div>

      <ChannelSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        channel={channel}
        canManage={canManageChannel}
        currentUserId={user?.id}
        onChannelUpdated={(c) => setChannel(c)}
        onChannelRemoved={() => { setSettingsOpen(false); window.location.href = "/app/office"; }}
      />

      <div ref={listRef} className="flex-1 overflow-y-auto scroll-thin px-6 py-4 space-y-1">
        {grouped.map((item, i) => item.divider ? (
          <div key={`d${i}`} className="text-center my-4">
            <span className="text-[11px] uppercase tracking-widest text-zinc-400 bg-white px-2">{item.divider}</span>
            <div className="border-t -mt-2 border-zinc-100"/>
          </div>
        ) : (
          <MessageItem key={item.id} msg={item} me={user?.id}
            onReact={react} onThread={openThread} onDelete={async () => { await api.delete(`/messages/${item.id}`); }}
            onEdit={async (t) => { await api.patch(`/messages/${item.id}`, { text: t }); }}
            onPin={async () => { await api.post(`/messages/${item.id}/pin`); toast.success("Pinned"); }}
            onSave={async () => { await api.post(`/messages/${item.id}/save`); toast.success("Saved"); }}
            onToTask={convertToTask}
          />
        ))}
        {Object.keys(typing).length > 0 && (
          <div className="text-xs text-zinc-500 italic px-2">{Object.values(typing).map((t) => t.name).join(", ")} typing…</div>
        )}
      </div>

      <div className="border-t border-[var(--border-subtle)] p-4 bg-white shrink-0">
        <div className="relative">
          {/* @mention autocomplete popup */}
          {mentionOpen && mentionCandidates.length > 0 && (
            <div
              className="absolute bottom-full left-0 mb-2 w-72 max-w-full rounded-lg border border-zinc-200 bg-white shadow-lg z-30 overflow-hidden"
              data-testid="mention-popup"
              role="listbox"
            >
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold border-b border-zinc-100 bg-zinc-50">
                Mention teammate
              </div>
              <div className="max-h-64 overflow-y-auto scroll-thin">
                {mentionCandidates.map((m, idx) => {
                  const isGroup = m.kind === "special" || m.kind === "department";
                  return (
                    <button
                      key={m.user_id}
                      type="button"
                      role="option"
                      aria-selected={idx === mentionIndex}
                      data-testid={`mention-option-${m.user_id}`}
                      onMouseDown={(e) => { e.preventDefault(); selectMention(m); }}
                      onMouseEnter={() => setMentionIndex(idx)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition ${
                        idx === mentionIndex ? "bg-orange-50" : "hover:bg-zinc-50"
                      }`}
                    >
                      {isGroup ? (
                        <span
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${
                            m.kind === "special"
                              ? "bg-[var(--brand-accent)] text-white"
                              : "bg-emerald-100 text-emerald-700"
                          }`}
                          aria-hidden
                        >
                          {m.kind === "special" ? "@" : "T"}
                        </span>
                      ) : (
                        <img src={renderUserAvatar(m.user)} className="w-7 h-7 rounded-full object-cover shrink-0" alt="" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-zinc-900 truncate">{m.user?.name || m.user?.email}</div>
                        <div className="text-[11px] text-zinc-500 truncate">
                          {m.department || m.role || m.user?.email}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="px-3 py-1 text-[10px] text-zinc-400 border-t border-zinc-100 bg-zinc-50">
                <kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">Enter</kbd> select · <kbd className="font-mono">Esc</kbd> close
              </div>
            </div>
          )}
          <div className="flex items-end gap-2 border border-zinc-200 rounded-lg p-2 focus-within:border-zinc-400">
            <AttachmentComposer onSend={uploadFile}/>
            <button
              type="button"
              onClick={() => setPollOpen(true)}
              className="p-1.5 hover:bg-zinc-100 rounded"
              aria-label="Create poll"
              title="Create poll"
              data-testid="poll-open-btn"
            >
              <BarChart3 className="w-4 h-4 text-zinc-500"/>
            </button>
            <textarea
              ref={textareaRef}
              data-testid="message-input"
              rows={1}
              placeholder={`Message #${channel.name}`}
              value={text}
              onChange={handleTextChange}
              onKeyDown={onKey}
              onBlur={() => setTimeout(closeMention, 120)}
              className="flex-1 resize-none outline-none bg-transparent text-sm max-h-40"
            />
            <Button size="sm" onClick={send} data-testid="message-send" className="bg-[var(--brand-accent)] text-white hover:bg-orange-700">
              <Send className="w-3.5 h-3.5"/>
            </Button>
          </div>
        </div>
      </div>
      <PollComposer open={pollOpen} onOpenChange={setPollOpen} onSubmit={createPoll}/>
    </div>
  );
}
